import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './GroupSuggestion.css';
import { useNavigate } from "./Router";
import { Button, Alert, Spinner } from 'reactstrap';
import { FaChevronLeft, FaChevronRight, FaUsers } from 'react-icons/fa';

const GroupSuggestion = ({onChange}) => {
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null);

    const [groups, setGroups] = useState([]);
    const [errors, setErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldDisplay, setShouldDisplay] = useState(true);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    // Vérification si l'appareil est mobile
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 992); // Considère les tablettes et plus petits comme mobile
        };

        // Vérification initiale
        checkIfMobile();

        // Ajouter un listener pour les changements de taille d'écran
        window.addEventListener('resize', checkIfMobile);

        // Nettoyage du listener
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const goGroup = (groupId) => {
        navigate("/group/" + groupId);
    };

    const fetchSugg = async () => {
        setIsLoading(true);
        try {
            const ps = await sdk.models.SocialGroup.suggestions({});
            if (!ps.error)
                setGroups(ps || []);
            setErrors(null);
        } catch (err) {
            console.error("Erreur lors du chargement des suggestions de groupes:", err);
            setErrors("Une erreur s'est produite lors du chargement des suggestions.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleJoin = async (groupId, event) => {
        event.stopPropagation();
        try {
            await sdk.models.GroupMember.create({
                group_id: groupId,
                active: 1,
                time: Math.floor(Date.now() / 1000),
            });

            // Mettre à jour l'état du groupe dans la liste
            setGroups((prevGroups) => 
                prevGroups.map(group => 
                    group.id === groupId ? {...group, joined: true} : group
                )
            );

            // Appeler le callback onChange si défini
            if (typeof onChange === "function") onChange();
        } catch (err) {
            console.error("Erreur lors de l'action 'Rejoindre':", err);
            setErrors("Une erreur s'est produite lors de l'action 'Rejoindre'. Veuillez réessayer.");
        }
    };

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const checkDisplayFrequency = () => {
        const lastDisplay = localStorage.getItem("groupSuggestionsLastDisplay");
        const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
        const now = Date.now();

        if (!lastDisplay || now - parseInt(lastDisplay, 10) > oneWeekInMs) {
            setShouldDisplay(true);
            localStorage.setItem("groupSuggestionsLastDisplay", now.toString());
        }
    };

    useEffect(() => {
        checkDisplayFrequency();
    }, []);

    useEffect(() => {
        if (shouldDisplay) {
            fetchSugg();
        }
    }, [shouldDisplay]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            // Vérifier initialement si on a besoin des flèches
            handleScroll();
        }
        
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, [groups]);

    // Ne pas afficher le composant s'il ne doit pas être affiché
    if (!shouldDisplay || (!isLoading && groups.length === 0)) return null;

    return (
        <div className="group-suggestions-container">
            <div className="group-suggestions-header">
                <h2 className="group-suggestions-title">Suggestions de groupes</h2>
                <Button color="link" onClick={fetchSugg} className="group-suggestions-refresh-btn">
                    Actualiser
                </Button>
            </div>
            
            {errors && (
                <Alert color="danger" className="group-suggestions-error">
                    {errors}
                </Alert>
            )}
            
            {isLoading ? (
                <div className="group-suggestions-loading">
                    <Spinner color="primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </Spinner>
                </div>
            ) : (
                <div className="group-suggestions-scroll-container-wrapper">
                    {showLeftArrow && (
                        <div 
                            className="group-suggestions-scroll-arrow group-suggestions-scroll-left"
                            onClick={scrollLeft}
                            aria-label="Défiler vers la gauche"
                        >
                            <FaChevronLeft />
                        </div>
                    )}
                    
                    <div 
                        className="group-suggestions-scroll-container" 
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                    >
                        {groups.map((group) => (
                            <div 
                                key={group.id}
                                className="group-card"
                                onClick={() => goGroup(group.id)}
                            >
                                <div className="group-avatar-wrapper">
                                    <img
                                        src={`https://peupleqc.com/s/peupleqc-upload/${group.cover}`}
                                        alt={`${group.group_title || 'Groupe'} cover`}
                                        className="group-avatar"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://peupleqc.com/s/peupleqc-upload/default-group-cover.jpg";
                                        }}
                                    />
                                </div>
                                <div className="group-info">
                                    <div className="group-info-top">
                                        <h3 
                                            className="group-title"
                                            title={group.group_title || 'Groupe'}
                                        >
                                            {group.group_title || 'Groupe'}
                                        </h3>
                                        
                                        {group.nbrLikes > 0 && (
                                            <small className="group-members">
                                                {group.nbrLikes} membre{group.nbrLikes > 1 ? 's' : ''}
                                            </small>
                                        )}
                                    </div>
                                    
                                    <button
                                        className={`join-button ${group.joined ? 'joined' : ''}`}
                                        onClick={(e) => handleJoin(group.id, e)}
                                        disabled={group.joined}
                                    >
                                        <FaUsers /> {group.joined ? 'Rejoint' : 'Rejoindre'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {showRightArrow && (
                        <div 
                            className="group-suggestions-scroll-arrow group-suggestions-scroll-right"
                            onClick={scrollRight}
                            aria-label="Défiler vers la droite"
                        >
                            <FaChevronRight />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export { GroupSuggestion };